<template>
  <div
    class="search-result-keyword"
    :class="{
      'page-hina': isHina,
      'page-jps': isJps,
    }"
  >
    <SearchNohit v-if="ss.state.result.hit == 0" :message="ss.state.result.message" />
    <template v-else>
      <nav class="item-search-result-nav" role="toolbar" aria-live="assertive">
        <h2 class="u-screen-reader-only">
          {{ lss('検索結果件数・並び替え', 'Search Result Number/Sort') }}
        </h2>
        <p class="jps-search-result-count is-size-7-touch">
          {{ lsst('{}件見つかりました。', '{} hit', num(ss.state.result.hit)) }}
        </p>
        <SearchPagenav class="jps-search-result-pagenavi" :ss="ss" />
        <div class="jps-search-select-options">
          <SearchPagesize :ss="ss" />
          <SearchSort :ss="ss" :options="sortOptions()" />
        </div>
        <!-- 表示形式 -->
        <div class="jps-display-mode" aria-hidden="true">
          <label class="is-size-7 is-hidden-mobile is-hidden-narrow">
            {{ lss('表示スタイル', 'Display mode') }}
          </label>
          <UiIconRadioButton
            v-model="localSearchStore.searchView"
            class="jps-icon-button"
            value="grid"
            id="grid"
            icon="mdi/view-dashboard-outline"
            :arialabel="ls(dapLangGlobal.crossSearch.thumbnailView)"
          />
          <UiIconRadioButton
            v-model="localSearchStore.searchView"
            class="jps-icon-button"
            value="panel"
            id="panel"
            icon="mdi/format-list-text"
            :arialabel="ls(dapLangGlobal.crossSearch.listView)"
          />
          <UiIconRadioButton
            v-model="localSearchStore.searchView"
            class="jps-icon-button"
            value="table"
            id="table"
            icon="mdi/table-large"
            :arialabel="ls(dapLangGlobal.crossSearch.tableView)"
          />
        </div>
      </nav>
      <div class="item-search-result-body" role="main" aria-live="assertive">
        <h2 class="u-screen-reader-only">{{ lss('検索結果', 'Search Result') }}</h2>
        <Transition name="fade">
          <UiItemSearchResultPanel
            v-if="localSearchStore.searchView === 'panel'"
            @show="emits('show', $event)"
            key="panel"
            class="type-panel"
            :test="test"
            :words="words"
            :ss="ss"
          />
          <UiItemSearchResultTable
            v-else-if="localSearchStore.searchView === 'table'"
            @show="emits('show', $event)"
            key="table"
            :test="test"
            :ss="ss"
            :columns="ss.state.searchdef.columns"
            :editstate="editstate"
          />
          <UiItemSearchResultGrid
            v-else-if="localSearchStore.searchView === 'grid'"
            @show="emits('show', $event)"
            key="grid"
            :test="test"
            :ss="ss"
          />
        </Transition>
      </div>
      <nav class="bottom-pagination">
        <div class="bottom-pagination-body">
          <SearchPagination :ss="ss" autoscroll />
        </div>
        <div v-if="!isHina && !isWebParts && !editstate && !nonote" class="search-to-note">
          <button
            class="jps-round-button jps-blue"
            @click="addNote()"
          >
            {{ lss('検索をノートに追加', 'Note Search Result') }}
          </button>
        </div>
      </nav>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import { useSearchResult } from '~/components/ui/item/search/useSearchResult'

const { isWebParts } = useWebParts()
const { ls, lss, num, lsst } = useI18n()
const { snackbarSuccess } = useSnackbar()
const localSearchStore = useLocalSearchStore()
const { getQueryExpression } = useCustomSearch()
const { addSearch } = useNoteStore()
const { isHina, isJps } = useServiceType()

interface Props {
  test?: boolean
  editstate: any
  ss: SearchStore<Item>
  nonote?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  test: false,
  nonote: false
})

const emits = defineEmits(['show'])

const { sortOptions } = useSearchResult(props.ss, props.test)

const addNote = async () => {
  if (props.ss.state.currentQuery && props.ss.state.currentQuery.csid) {
    const qe = await getQueryExpression(props.ss.state.currentQuery)
    const title = abbText(qe.toString(ls), 100)
    await addSearch(title, props.ss.state.currentQuery)
    snackbarSuccess(
      { ja: title + 'を保存しました', en: title + ' was saved' },
    )
  }
}

const words = computed(() => {
  const words = []
  if (props.ss) {
    if (props.ss.state.keyword)
      props.ss.state.keyword.filter(k => k).forEach(k => words.push(k))
    if (props.ss.state.query)
      Object.keys(props.ss.state.query).forEach(key => {
        props.ss.state.query[key].filter(k => k).forEach(w => words.push(w))
      })
  }
  return words
})
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.search-result-keyword {
  .item-search-result-nav {
    display: grid;
    gap: 1rem;

    @include is-mobile {
      grid-template:
        "count count" auto
        "pagenavi pagenavi" auto
        "options mode" auto / 1fr auto;
    }

    @include is-tablet {
      grid-template:
        "count pagenavi" auto
        "options mode" auto / 1fr auto;
    }

    @include is-desktop {
      grid-template:
        "count pagenavi options mode" auto / 1fr auto auto auto;
    }

    .jps-search-result-count {
      grid-area: count;
      display: flex;
      align-items: center;
    }

    .jps-search-result-pagenavi {
      grid-area: pagenavi;
    }

    .jps-search-select-options {
      display: flex;
      align-items: center;
      grid-area: options;

      :deep(> div:first-child) {
        margin-left: -0.25rem;
      }

      :deep(select) {
        padding: 0 1.5rem 0 0.5rem;
      }
    }

    .jps-display-mode {
      display: flex;
      align-items: center;
      grid-area: mode;
      gap: 1.125rem;
      margin-left: auto;
    }
  }
  .item-search-result-body {
    .type-panel {
      margin-top: 1.5rem;
    }
  }
}

.bottom-pagination {

  .bottom-pagination-body {
    flex-grow: 1;
    margin-top: 1rem;
  }

  .search-to-note {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin-top: 1rem;
    @include is-tablet {
      flex-direction: row;
      justify-content: flex-end;
    }
    .jps-round-button {
      font-size: 0.75rem;
      line-height: calc(1.75 / 1.25);
      align-items: center;
      @include is-tablet {
        font-size: 1rem;
        line-height: 1.5;
      }
    }
  }
}

.page-jps {
  .bottom-pagination {
    @include is-tablet {
      display: flex;
      width: 100%;
    }

    .bottom-pagination-body {
      @include is-tablet {
        // NOTE: 「検索をノートに追加」ボタンの幅 * 0.5
        transform: translateX(84px);
      }
    }
  }
}
</style>
